import OmSpinner from "@/components/OmSpinner";
import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings, keyForPath } from "@/context/SettingsContext";
import { fetchContent, fetchFilters } from "@/lib/api";
import { CATEGORY_BY_TYPE } from "@/lib/categories";
import { heroForType } from "@/lib/heroes";
import KnowledgeCard from "@/components/KnowledgeCard";
import { PageHero } from "@/components/PageHero";
import RelatedServices from "@/components/RelatedServices";
import Seo from "@/components/Seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

const FILTER_DIMS = {
  mantra: ["deity", "tradition", "purpose"],
  puja_vidhi: ["deity", "occasion", "category"],
  katha: ["category", "deity"],
  stotra: ["deity", "tradition"],
  vedic: ["category", "tradition"],
  festival: ["occasion"],
};

export default function CategoryPage({ type }) {
  const { t, lang, pick } = useLang();
  const { label, heading } = useSettings();
  const { pathname } = useLocation();
  const cat = CATEGORY_BY_TYPE[type];
  const secLabel = label(keyForPath(pathname), cat.deva, cat.en);
  const hd = heading(keyForPath(pathname), { title_deva: secLabel.deva, title_en: secLabel.en, sub_deva: cat.tagline_hi, sub_en: cat.tagline_en });
  const [items, setItems] = useState([]);
  const [filterOpts, setFilterOpts] = useState({});
  const [active, setActive] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback((filters) => {
    setLoading(true);
    fetchContent({ type, ...filters })
      .then(setItems)
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    setActive({});
    load({});
    fetchFilters(type).then(setFilterOpts).catch(() => {});
  }, [type, load]);

  const dims = (FILTER_DIMS[type] || []).filter((d) => (filterOpts[d] || []).length > 0);
  const dimLabel = (dim) => t(`filter_${dim}`);

  const setFilter = (dim, value) => {
    const next = { ...active };
    if (value === "__all__" || active[dim] === value) delete next[dim];
    else next[dim] = value;
    setActive(next);
    load(next);
  };

  const clearAll = () => { setActive({}); load({}); };

  return (
    <>
      <Seo path={cat.path} />
      <PageHero
        art={heroForType(type)}
        overline={hd.title_en}
        deva={hd.title_deva}
        tagline={lang === "hi" ? hd.sub_deva : hd.sub_en}
      />


      <section className="mx-auto max-w-7xl px-5 py-12 md:px-8 md:py-16">
        {type === "mantra" && (
          <Link to="/japa" data-testid="mantras-japa-banner"
            className="group mb-10 flex flex-col items-start gap-4 overflow-hidden rounded-2xl border border-[var(--gold-muted)]/40 bg-gradient-to-br from-[rgba(199,80,0,0.07)] to-[rgba(184,134,43,0.05)] p-5 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg sm:flex-row sm:items-center sm:gap-5 md:p-6">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[var(--saffron)]/12 font-deva text-4xl text-saffron ring-1 ring-[var(--saffron)]/25">ॐ</span>
            <div className="min-w-0 flex-1">
              <p className="font-serif-latin text-xl leading-tight text-ink md:text-2xl">{pick("जप करें — १०८ मनका माला", "Japa — count your mala")}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{pick("किसी भी मंत्र का जप करें और अपनी माला गिनें — डिजिटल जप-माला के साथ।", "Chant any mantra and count your rounds with the digital japa mala.")}</p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-transform group-hover:translate-x-0.5">{pick("जप प्रारंभ करें", "Start Japa")}</span>
          </Link>
        )}
        {dims.length > 0 && (
          <div className="mb-10 flex flex-wrap items-center gap-3">
            <span className="text-sm text-ink-3">{t("filters")}:</span>
            {dims.map((dim) => (
              <Select key={dim} value={active[dim] || "__all__"} onValueChange={(v) => setFilter(dim, v)}>
                <SelectTrigger data-testid={`filter-${dim}`} className="w-auto min-w-[9rem] rounded-none border-line bg-surface">
                  <SelectValue placeholder={dimLabel(dim)} />
                </SelectTrigger>
                <SelectContent className="rounded-none border-line bg-surface">
                  <SelectItem value="__all__">{dimLabel(dim)}: {t("all")}</SelectItem>
                  {(filterOpts[dim] || []).map((opt) => {
                    const val = typeof opt === "string" ? opt : opt.value;
                    const lbl = typeof opt === "string" ? opt : opt.label;
                    return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            ))}
            {Object.keys(active).length > 0 && (
              <button onClick={clearAll} data-testid="clear-filters" className="inline-flex items-center gap-1 text-sm text-saffron">
                <X className="h-3.5 w-3.5" /> {t("clear")}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <OmSpinner />
        ) : items.length === 0 ? (
          <p className="py-20 text-center text-ink-3">{t("no_results")}</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it) => (
              <KnowledgeCard key={it.slug} item={it} showImage />
            ))}
          </div>
        )}

        <RelatedServices type={type} />
      </section>
    </>
  );
}
